/**
 * Template Validator
 *
 * Validates a raw JSON object against the CSV importer template schema.
 * Returns either a normalized template (with all defaults applied) or
 * a list of structured validation errors.
 *
 * Key behaviors:
 * - Collects ALL errors in a single pass (doesn't stop at first error)
 * - Never mutates the input object
 * - Returns a normalized copy with defaults applied on success
 * - Pre-compiles regex patterns during normalization
 */

import type {
  ColumnType,
  TemplateValidationResult,
  ValidationError,
  ValidationWarning,
  NormalizedTemplate,
  NormalizedImporterConfig,
  NormalizedColumnDefinition,
  NormalizedLimitsConfig,
  PapaParsePassthrough,
} from '../types/template';

import { compileRegex } from './regex-validator';
import {
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_ROWS,
  PROTECTED_PAPAPARSE_KEYS,
  SUPPORTED_COLUMN_TYPES,
  SUPPORTED_DATE_FORMATS,
  SUPPORTED_TRANSFORMS,
} from '../constants/default';

/** Basic semver format: major.minor.patch (e.g., "1.0.0", "2.3.11") */
const SEMVER_REGEX = /^\d+\.\d+\.\d+$/;

/**
 * Validates a template object against the schema.
 *
 * @param input - Raw template object (typed as unknown since it comes from host app)
 * @returns Discriminated union: { valid: true, template, warnings } or { valid: false, errors }
 */
export function validate(input: unknown): TemplateValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Must be a non-null object
  if (input === null || typeof input !== 'object' || Array.isArray(input)) {
    errors.push({
      path: '',
      message: 'Template must be a non-null object',
      code: 'INVALID_TEMPLATE',
    });
    return { valid: false, errors };
  }

  const template = input as Record<string, unknown>;

  // ---- version ----
  if (typeof template.version !== 'string' || template.version.trim() === '') {
    errors.push({
      path: 'version',
      message:
        'Template must have a \'version\' property of type string in semver format (e.g., "1.0.0")',
      code: 'MISSING_VERSION',
    });
  } else if (!SEMVER_REGEX.test(template.version)) {
    warnings.push({
      path: 'version',
      message: `Version '${template.version}' is not in semver format (expected "major.minor.patch", e.g., "1.0.0")`,
      code: 'INVALID_VERSION_FORMAT',
    });
  }

  // ---- importerConfig ----
  if (
    template.importerConfig === undefined ||
    template.importerConfig === null ||
    typeof template.importerConfig !== 'object' ||
    Array.isArray(template.importerConfig)
  ) {
    errors.push({
      path: 'importerConfig',
      message: "Template is missing required property 'importerConfig'",
      code: 'MISSING_IMPORTER_CONFIG',
    });
    // Can't validate further without importerConfig
    return { valid: false, errors };
  }

  const importerConfig = template.importerConfig as Record<string, unknown>;

  // importerConfig.type
  if (
    typeof importerConfig.type !== 'string' ||
    importerConfig.type.trim() === ''
  ) {
    errors.push({
      path: 'importerConfig.type',
      message: "importerConfig must have a 'type' property of type string",
      code: 'MISSING_IMPORTER_CONFIG_TYPE',
    });
  }

  // importerConfig.title
  if (
    typeof importerConfig.title !== 'string' ||
    importerConfig.title.trim() === ''
  ) {
    errors.push({
      path: 'importerConfig.title',
      message: "importerConfig must have a 'title' property of type string",
      code: 'MISSING_IMPORTER_CONFIG_TITLE',
    });
  }

  // importerConfig.allowUnmappedColumns (optional, must be boolean if present)
  if (
    importerConfig.allowUnmappedColumns !== undefined &&
    typeof importerConfig.allowUnmappedColumns !== 'boolean'
  ) {
    errors.push({
      path: 'importerConfig.allowUnmappedColumns',
      message: 'importerConfig.allowUnmappedColumns must be a boolean',
      code: 'INVALID_ALLOW_UNMAPPED_COLUMNS',
    });
  }

  // importerConfig.limits (optional)
  if (importerConfig.limits !== undefined) {
    if (
      typeof importerConfig.limits !== 'object' ||
      importerConfig.limits === null ||
      Array.isArray(importerConfig.limits)
    ) {
      errors.push({
        path: 'importerConfig.limits',
        message: 'importerConfig.limits must be an object',
        code: 'INVALID_LIMITS',
      });
    } else {
      const limits = importerConfig.limits as Record<string, unknown>;

      if (limits.maxFileSize !== undefined) {
        if (
          typeof limits.maxFileSize !== 'number' ||
          !Number.isInteger(limits.maxFileSize) ||
          limits.maxFileSize <= 0
        ) {
          errors.push({
            path: 'importerConfig.limits.maxFileSize',
            message:
              'importerConfig.limits.maxFileSize must be a positive integer',
            code: 'INVALID_LIMITS',
          });
        }
      }

      if (limits.maxRows !== undefined) {
        if (
          typeof limits.maxRows !== 'number' ||
          !Number.isInteger(limits.maxRows) ||
          limits.maxRows <= 0
        ) {
          errors.push({
            path: 'importerConfig.limits.maxRows',
            message: 'importerConfig.limits.maxRows must be a positive integer',
            code: 'INVALID_LIMITS',
          });
        }
      }
    }
  }

  // importerConfig.parserOptions (optional)
  if (importerConfig.parserOptions !== undefined) {
    if (
      typeof importerConfig.parserOptions !== 'object' ||
      importerConfig.parserOptions === null ||
      Array.isArray(importerConfig.parserOptions)
    ) {
      errors.push({
        path: 'importerConfig.parserOptions',
        message: 'importerConfig.parserOptions must be an object',
        code: 'INVALID_PARSER_OPTIONS',
      });
    } else {
      const parserOpts = importerConfig.parserOptions as Record<
        string,
        unknown
      >;
      for (const key of PROTECTED_PAPAPARSE_KEYS) {
        if (key in parserOpts) {
          warnings.push({
            path: `importerConfig.parserOptions.${key}`,
            message: `'${key}' is a protected PapaParse key and will be ignored`,
            code: 'PROTECTED_PARSER_OPTION',
          });
        }
      }
    }
  }

  // ---- columns ----
  if (!Array.isArray(template.columns) || template.columns.length === 0) {
    errors.push({
      path: 'columns',
      message: "Template must have a non-empty 'columns' array",
      code: 'MISSING_COLUMNS',
    });
    return { valid: false, errors };
  }

  const columns = template.columns as Record<string, unknown>[];
  const seenKeys = new Map<string, number>();

  columns.forEach((col, index) => {
    const prefix = `columns[${index}]`;

    // Required properties
    if (typeof col.key !== 'string' || col.key.trim() === '') {
      errors.push({
        path: `${prefix}.key`,
        message: `${prefix} is missing required property 'key'`,
        code: 'MISSING_COLUMN_PROPERTY',
      });
    } else {
      // Check for duplicate keys
      if (seenKeys.has(col.key as string)) {
        errors.push({
          path: `${prefix}.key`,
          message: `Duplicate column key '${col.key}' found at columns[${seenKeys.get(col.key as string)}] and ${prefix}`,
          code: 'DUPLICATE_COLUMN_KEY',
        });
      } else {
        seenKeys.set(col.key as string, index);
      }
    }

    if (typeof col.label !== 'string' || col.label.trim() === '') {
      errors.push({
        path: `${prefix}.label`,
        message: `${prefix} is missing required property 'label'`,
        code: 'MISSING_COLUMN_PROPERTY',
      });
    }

    if (typeof col.required !== 'boolean') {
      errors.push({
        path: `${prefix}.required`,
        message: `${prefix} is missing required property 'required'`,
        code: 'MISSING_COLUMN_PROPERTY',
      });
    }

    if (
      typeof col.type !== 'string' ||
      !SUPPORTED_COLUMN_TYPES.has(col.type as string)
    ) {
      const typeVal =
        typeof col.type === 'string' ? col.type : String(col.type);
      errors.push({
        path: `${prefix}.type`,
        message: `${prefix} has unsupported type '${typeVal}'. Supported: string, number, email, date, boolean, phone, custom`,
        code: 'INVALID_COLUMN_TYPE',
      });
    }

    // Regex validation
    if (col.regex !== undefined) {
      if (
        typeof col.regex !== 'object' ||
        col.regex === null ||
        Array.isArray(col.regex)
      ) {
        errors.push({
          path: `${prefix}.regex`,
          message: `${prefix}.regex must be an object with 'pattern' and 'message' properties`,
          code: 'INVALID_REGEX',
        });
      } else {
        const regex = col.regex as Record<string, unknown>;
        let patternValid = true;

        if (typeof regex.pattern === 'string') {
          try {
            compileRegex(regex.pattern);
          } catch {
            patternValid = false;
            errors.push({
              path: `${prefix}.regex.pattern`,
              message: `${prefix}.regex.pattern is not a valid regular expression`,
              code: 'INVALID_REGEX',
            });
          }
        } else {
          patternValid = false;
          errors.push({
            path: `${prefix}.regex.pattern`,
            message: `${prefix}.regex.pattern must be a string`,
            code: 'INVALID_REGEX',
          });
        }

        // Only validate message if pattern is valid — avoids noisy double-errors
        if (patternValid) {
          if (
            typeof regex.message !== 'string' ||
            regex.message.trim() === ''
          ) {
            errors.push({
              path: `${prefix}.regex.message`,
              message: `${prefix}.regex.message must be a non-empty string`,
              code: 'INVALID_REGEX_MESSAGE',
            });
          }
        }
      }
    }

    // Transform validation
    if (col.transform !== undefined) {
      if (
        typeof col.transform !== 'string' ||
        !SUPPORTED_TRANSFORMS.has(col.transform as string)
      ) {
        errors.push({
          path: `${prefix}.transform`,
          message: `${prefix}.transform must be one of: trim, uppercase, lowercase, none`,
          code: 'INVALID_TRANSFORM',
        });
      }
    }

    // Options validation
    if (col.options !== undefined) {
      if (
        !Array.isArray(col.options) ||
        !(col.options as unknown[]).every((item) => typeof item === 'string')
      ) {
        errors.push({
          path: `${prefix}.options`,
          message: `${prefix}.options must be an array of strings`,
          code: 'INVALID_OPTIONS',
        });
      }
    }

    // dateFormat validation
    if (col.dateFormat !== undefined) {
      if (
        typeof col.dateFormat !== 'string' ||
        !SUPPORTED_DATE_FORMATS.has(col.dateFormat as string)
      ) {
        const supported = Array.from(SUPPORTED_DATE_FORMATS).join(', ');
        warnings.push({
          path: `${prefix}.dateFormat`,
          message: `${prefix}.dateFormat '${String(col.dateFormat)}' is not a recognized format. Supported formats: ${supported}`,
          code: 'UNSUPPORTED_DATE_FORMAT',
        });
      }
    }

    // Error if both options and regex are present — template author must remove one
    if (col.options !== undefined && col.regex !== undefined) {
      errors.push({
        path: prefix,
        message: `${prefix} has both 'options' and 'regex'. A column cannot have both — remove one.`,
        code: 'REGEX_IGNORED_WITH_OPTIONS',
      });
    }

    // Unique validation: must be boolean if present
    if (col.unique !== undefined && typeof col.unique !== 'boolean') {
      errors.push({
        path: `${prefix}.unique`,
        message: `${prefix}.unique must be a boolean`,
        code: 'INVALID_UNIQUE',
      });
    }

    // Unique + default conflict: unique columns cannot have a default value
    if (col.unique === true && col.default !== undefined) {
      errors.push({
        path: prefix,
        message: `${prefix} cannot have both 'unique' and 'default'. A unique column cannot have a default value.`,
        code: 'UNIQUE_WITH_DEFAULT',
      });
    }
  });

  // If any errors, return them
  if (errors.length > 0) {
    return { valid: false, errors };
  }

  // ---- Normalization ----
  const normalizedTemplate = normalize(
    template,
    importerConfig,
    columns,
    warnings,
  );

  return { valid: true, template: normalizedTemplate, warnings };
}

/**
 * Normalizes a validated template — applies defaults and pre-compiles regex.
 * Only called when validation passes (no errors).
 */
function normalize(
  template: Record<string, unknown>,
  importerConfig: Record<string, unknown>,
  columns: Record<string, unknown>[],
  _warnings: ValidationWarning[],
): NormalizedTemplate {
  const limits = (importerConfig.limits ?? {}) as Record<string, unknown>;

  const normalizedLimits: NormalizedLimitsConfig = {
    maxFileSize:
      typeof limits.maxFileSize === 'number'
        ? limits.maxFileSize
        : DEFAULT_MAX_FILE_SIZE,
    maxRows:
      typeof limits.maxRows === 'number' ? limits.maxRows : DEFAULT_MAX_ROWS,
  };

  // Strip protected PapaParse keys
  let normalizedParserOptions: PapaParsePassthrough | undefined;
  if (importerConfig.parserOptions !== undefined) {
    const parserOpts = importerConfig.parserOptions as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(parserOpts)) {
      if (
        !PROTECTED_PAPAPARSE_KEYS.includes(
          key as (typeof PROTECTED_PAPAPARSE_KEYS)[number],
        )
      ) {
        sanitized[key] = value;
      }
    }

    normalizedParserOptions = sanitized as PapaParsePassthrough;
  }

  const normalizedConfig: NormalizedImporterConfig = {
    type: importerConfig.type as string,
    title: importerConfig.title as string,
    allowUnmappedColumns:
      typeof importerConfig.allowUnmappedColumns === 'boolean'
        ? importerConfig.allowUnmappedColumns
        : false,
    limits: normalizedLimits,
    ...(normalizedParserOptions !== undefined && {
      parserOptions: normalizedParserOptions,
    }),
  };

  const normalizedColumns: NormalizedColumnDefinition[] = columns.map((col) => {
    const hasOptions = Array.isArray(col.options);
    const hasRegex =
      col.regex !== undefined &&
      typeof (col.regex as Record<string, unknown>).pattern === 'string';

    // Pre-compile regex (skip if options present — regex is ignored)
    let compiledRegex: RegExp | undefined;
    if (hasRegex && !hasOptions) {
      try {
        compiledRegex = compileRegex(
          (col.regex as Record<string, unknown>).pattern as string,
        );
      } catch {
        // Already validated — shouldn't happen
      }
    }

    return {
      key: col.key as string,
      label: col.label as string,
      required: col.required as boolean,
      type: col.type as ColumnType,
      unique: (col.unique as boolean) ?? false,
      transform:
        (col.transform as 'trim' | 'uppercase' | 'lowercase' | 'none') ??
        'none',
      ...(col.regex !== undefined && { regex: col.regex }),
      ...(col.sampleData !== undefined && {
        sampleData: col.sampleData as string,
      }),
      ...(col.dateFormat !== undefined && {
        dateFormat: col.dateFormat as string,
      }),
      ...(col.description !== undefined && {
        description: col.description as string,
      }),
      ...(col.default !== undefined && { default: col.default as string }),
      ...(col.options !== undefined && { options: col.options as string[] }),
      ...(compiledRegex !== undefined && { compiledRegex }),
    } as NormalizedColumnDefinition;
  });

  return {
    version: template.version as string,
    importerConfig: normalizedConfig,
    columns: normalizedColumns,
  };
}
